<template>
  <div class="card" v-loading="loading">
    <div class="card-content">

      <!-- ================= GENERAL ================= -->
      <div v-show="section === 'general'">
        <div class="row">
          <div class="col-md-8">
            <div class="row">
              <div class="col-md-8">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.name') }}*</label>
                  <input type="text" class="form-control" maxlength="255" v-model="form.name" :placeholder="$t('menuSubscriptions.namePlaceholder')">
                  <small class="text-danger" v-if="errors.name">{{ errors.name }}</small>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.active') }}</label>
                  <br/>
                  <p-switch v-model="form.active" type="success" :disabled="!subscriptionId"></p-switch>
                  <small v-if="!subscriptionId" class="text-muted msub-block">{{ $t('menuSubscriptions.activeAfterMenus') }}</small>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.location') }}*</label>
                  <el-select v-model="form.location_id" class="w-100" filterable :placeholder="$t('menuSubscriptions.locationPlaceholder')" @change="onLocationChange">
                    <el-option v-for="location in locations" :key="location.id" :label="location.name" :value="location.id"></el-option>
                  </el-select>
                  <small class="text-muted msub-block">{{ $t('menuSubscriptions.locationHint') }}</small>
                  <small class="text-danger" v-if="errors.location_id">{{ errors.location_id }}</small>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.pricePerDay') }}* <small class="text-muted">({{ currency }})</small></label>
                  <input type="number" min="0" step="0.01" class="form-control" v-model="form.price_per_day">
                  <small class="text-muted msub-block">{{ $t('menuSubscriptions.pricePerDayHint') }}</small>
                  <small class="text-danger" v-if="errors.price_per_day">{{ errors.price_per_day }}</small>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.duration') }}*</label>
                  <input type="number" min="1" max="366" step="1" class="form-control msub-duration-input" v-model="form.duration_days">
                  <small class="text-danger msub-block" v-if="errors.duration_days">{{ errors.duration_days }}</small>
                  <div class="msub-note">
                    <div>{{ $t('menuSubscriptions.durationHint', { days: maxStartDaysAhead }) }}</div>
                    <div>{{ $t('menuSubscriptions.durationWeekendHint') }}</div>
                    <div v-if="deliveryEstimate" class="msub-note__estimate">
                      <i class="fa fa-calendar-check-o"></i>
                      {{ $t('menuSubscriptions.durationEstimate', { range: deliveryEstimate, days: form.duration_days }) }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.maxSubscribers') }}</label>
                  <input type="number" min="0" step="1" class="form-control" v-model="form.max_active_subscribers" :placeholder="$t('menuSubscriptions.unlimited')">
                  <small class="text-muted msub-block">{{ $t('menuSubscriptions.maxSubscribersHint') }}</small>
                  <small class="text-danger" v-if="errors.max_active_subscribers">{{ errors.max_active_subscribers }}</small>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.minQuantity') }}</label>
                  <input type="number" min="1" max="50" step="1" class="form-control" v-model="form.min_quantity">
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>{{ $t('menuSubscriptions.maxQuantity') }}</label>
                  <input type="number" min="1" max="50" step="1" class="form-control" v-model="form.max_quantity">
                </div>
              </div>
              <div class="col-md-12">
                <small class="text-muted msub-block">{{ $t('menuSubscriptions.quantityHint') }}</small>
                <small class="text-danger" v-if="errors.quantity">{{ errors.quantity }}</small>
              </div>
            </div>

            <div class="form-group msub-mt">
              <label>{{ $t('menuSubscriptions.description') }}</label>
              <wysiwyg v-if="loaded" v-model="form.description" :options="editorOptions" class="form-control msub-editor" />
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group">
              <label>{{ $t('menuSubscriptions.image') }} <small class="text-muted">({{ $t('menuSubscriptions.imageHint') }})</small></label>
              <el-upload
                action=""
                list-type="picture-card"
                accept=".jpg,.jpeg,.png"
                :file-list="form.imagelist"
                :limit="1"
                :auto-upload="false"
                :on-change="handleImageChange"
                :on-remove="handleImageRemove"
                :on-exceed="handleExceed">
                <img src="/static/img/icons/icon-plus-primary.png">
              </el-upload>
              <small class="text-danger" v-if="errors.image">{{ errors.image }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= DELIVERY ================= -->
      <div v-show="section === 'delivery'">
        <div class="form-group">
          <label>{{ $t('menuSubscriptions.deliveryDays') }}*</label>
          <div class="msub-days">
            <label v-for="day in dayNames" :key="day" class="msub-day" :class="{ 'is-checked': form.delivery_days.indexOf(day) !== -1 }">
              <input type="checkbox" :value="day" v-model="form.delivery_days"> {{ $t('days.' + day) }}
            </label>
          </div>
          <small class="text-muted msub-block">{{ $t('menuSubscriptions.deliveryDaysHint') }}</small>
          <small class="text-danger" v-if="errors.delivery_days">{{ errors.delivery_days }}</small>
        </div>

        <div class="form-group">
          <label>{{ $t('menuSubscriptions.deliveryHours') }}*</label>
          <div v-for="(interval, index) in form.delivery_hours" :key="'hour' + index" class="msub-hour">
            <el-time-select v-model="interval.from" :picker-options="pickerFrom" :placeholder="$t('menuSubscriptions.from')"></el-time-select>
            <span>-</span>
            <el-time-select v-model="interval.to" :picker-options="pickerTo" :placeholder="$t('menuSubscriptions.to')"></el-time-select>
            <a class="msub-icon msub-icon--danger" :title="$t('general.delete')" @click="form.delivery_hours.splice(index, 1)"><i class="fa fa-trash-o"></i></a>
          </div>
          <a class="btn btn-sm btn-default" @click="addHour"><i class="fa fa-plus"></i> {{ $t('menuSubscriptions.addInterval') }}</a>
          <small class="text-muted msub-block">{{ $t('menuSubscriptions.deliveryHoursHint') }}</small>
          <small class="text-danger msub-block" v-if="errors.delivery_hours">{{ errors.delivery_hours }}</small>
        </div>

        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label>{{ $t('menuSubscriptions.orderLead') }}*</label>
              <div class="input-group">
                <input type="number" min="0" max="2880" step="5" class="form-control" v-model="form.order_lead_minutes">
                <span class="input-group-addon">{{ $t('menuSubscriptions.minutes') }}</span>
              </div>
              <small class="text-danger" v-if="errors.order_lead_minutes">{{ errors.order_lead_minutes }}</small>
            </div>
          </div>
          <div class="col-md-8">
            <p class="text-muted msub-lead-hint">{{ orderLeadHint }}</p>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('menuSubscriptions.zones') }}*</label>
          <p v-if="!form.location_id" class="text-muted">{{ $t('menuSubscriptions.zonesPickLocation') }}</p>
          <p v-else-if="!zones.length" class="text-muted">{{ $t('menuSubscriptions.noZones') }}</p>
          <div v-else class="msub-zones">
            <div class="msub-zone msub-zone--head">
              <span></span>
              <span>{{ $t('menuSubscriptions.zone') }}</span>
              <span>{{ $t('menuSubscriptions.zoneDefaultFee') }}</span>
              <span>{{ $t('menuSubscriptions.zoneFee') }}</span>
            </div>
            <div v-for="zone in zones" :key="'zone' + zone.id" class="msub-zone" :class="{ 'is-checked': isZoneSelected(zone.id) }">
              <input type="checkbox" :checked="isZoneSelected(zone.id)" @change="toggleZone(zone)">
              <span @click="toggleZone(zone)" class="msub-zone__name">
                {{ zone.name }}
                <small class="text-muted">· {{ zone.company_zone ? $t('menuSubscriptions.companyZone') : $t('menuSubscriptions.locationZone') }}</small>
                <span v-if="!zone.active" class="msub-badge">{{ $t('menuSubscriptions.zoneInactive') }}</span>
              </span>
              <span>{{ zone.shipping_cost }} {{ currency }}</span>
              <span>
                <input v-if="isZoneSelected(zone.id)"
                       type="number" min="0" step="0.01"
                       class="form-control input-sm"
                       :placeholder="zone.shipping_cost"
                       v-model="selectedZone(zone.id).delivery_fee">
              </span>
            </div>
          </div>
          <small class="text-muted msub-block">{{ $t('menuSubscriptions.zonesHint') }}</small>
          <small class="text-danger msub-block" v-if="errors.zones">{{ errors.zones }}</small>
        </div>
      </div>

      <div class="text-center mar-to-10">
        <button class="btn btn-purple-light btn-fill btn-wd" :disabled="saving" @click.prevent="save()">{{ $t('general.save') }}</button>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Upload, Select, Option, TimeSelect} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import 'vue-wysiwyg/dist/vueWysiwyg.css'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import { DAY_NAMES } from './menuSubscriptionHelpers'

  Vue.use(Upload)
  Vue.use(Select)
  Vue.use(Option)
  Vue.use(TimeSelect)

  const MAX_IMAGE_MB = 5

  export default {
    components: {
      PSwitch
    },
    props: {
      subscriptionId: {
        type: Number,
        default: null
      },
      // 'general' | 'delivery'
      section: {
        type: String,
        default: 'general'
      }
    },
    data () {
      return {
        dayNames: DAY_NAMES,
        // no inline images (they'd be stored as base64 in the description)
        editorOptions: {
          hideModules: { code: true, table: true, image: true }
        },
        pickerFrom: { start: '00:00', step: '00:15', end: '23:45' },
        pickerTo: { start: '00:15', step: '00:15', end: '24:00' },
        loading: false,
        loaded: false,
        saving: false,
        currency: '',
        maxStartDaysAhead: 10,
        locations: [],
        zones: [],
        // last saved state (price change -> ask to rescale the menus)
        savedPrice: null,
        menusCount: 0,
        form: {
          name: '',
          active: false,
          location_id: '',
          price_per_day: '',
          duration_days: 30,
          min_quantity: 1,
          max_quantity: 5,
          max_active_subscribers: '',
          description: '',
          imagelist: [],
          delivery_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          delivery_hours: [{ from: '12:00', to: '13:00' }],
          order_lead_minutes: 120,
          zones: []
        },
        errors: {}
      }
    },
    computed: {
      // delivery days in a period of duration_days with the ticked delivery days:
      // fewest / most over the 7 possible start weekdays ('21' or '21-22'), '' when not computable
      deliveryEstimate () {
        const days = parseInt(this.form.duration_days, 10)
        const delivery = this.form.delivery_days || []
        if (!days || days < 1 || days > 366 || !delivery.length) {
          return ''
        }
        let min = null
        let max = null
        for (let start = 0; start < 7; start++) {
          let count = 0
          for (let i = 0; i < days; i++) {
            if (delivery.indexOf(DAY_NAMES[(start + i) % 7]) !== -1) {
              count++
            }
          }
          min = min === null ? count : Math.min(min, count)
          max = max === null ? count : Math.max(max, count)
        }
        return min === max ? String(min) : min + '-' + max
      },
      orderLeadHint () {
        const minutes = parseInt(this.form.order_lead_minutes, 10) || 0
        const first = this.form.delivery_hours.find(hour => hour.from) || { from: '12:00' }
        const parts = first.from.split(':')
        let total = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10) - minutes
        let dayBefore = false
        while (total < 0) {
          total += 1440
          dayBefore = true
        }
        const time = String(Math.floor(total / 60)).padStart(2, '0') + ':' + String(total % 60).padStart(2, '0')
        return this.$t(dayBefore ? 'menuSubscriptions.orderLeadExampleDayBefore' : 'menuSubscriptions.orderLeadExample', { from: first.from, time })
      }
    },
    created () {
      this.currency = this.getCurrencySymbol(this.$session.get('selectedCompanyData').currency_iso)
      // value = location TOKEN (the API returns location_token and accepts token or numeric id)
      this.locations = (this.$session.get('selectedCompanyData').locations_details || [])
        .map(location => ({ id: location.token || location.id, name: location.name }))
      if (this.subscriptionId) {
        this.getSubscription()
      } else {
        if (this.locations.length === 1) {
          this.form.location_id = this.locations[0].id
          this.getZones()
        }
        this.loaded = true
      }
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      getZones () {
        if (!this.form.location_id) {
          this.zones = []
          return
        }
        this.apiRequest('/menu-subscription/zones', 'GET', { ...this.requestData(), location_id: this.form.location_id }, (response) => {
          if (response.status === true) {
            this.zones = response.data || []
            // drop the zones that don't belong to this location
            const ids = this.zones.map(zone => zone.id)
            this.form.zones = this.form.zones.filter(zone => ids.indexOf(zone.delivery_range_id) !== -1)
          }
        })
      },
      onLocationChange () {
        this.getZones()
      },
      getSubscription () {
        this.loading = true
        this.apiRequest('/menu-subscription/' + this.subscriptionId, 'GET', this.requestData(), (response) => {
          this.loading = false
          if (!(response.status === true && response.data)) {
            swal('', this.firstError(response), 'error').catch(() => {})
            return
          }
          this.fillForm(response.data)
          this.$emit('loaded', response.data)
          this.getZones()
          this.loaded = true
        })
      },
      fillForm (data) {
        this.savedPrice = data.price_per_day
        this.menusCount = data.menus_count || 0
        this.maxStartDaysAhead = data.max_start_days_ahead || 10
        this.form = {
          name: data.name || '',
          active: !!data.active,
          location_id: data.location_token || '',
          price_per_day: data.price_per_day,
          duration_days: data.duration_days,
          min_quantity: data.min_quantity,
          max_quantity: data.max_quantity,
          max_active_subscribers: data.max_active_subscribers || '',
          description: data.description || '',
          imagelist: data.image_filename
            ? [{ url: data.image_url, name: data.image_filename, filename: data.image_filename }]
            : [],
          delivery_days: (data.delivery_days || []).slice(),
          delivery_hours: (data.delivery_hours || []).map(hour => ({ from: hour.from, to: hour.to })),
          order_lead_minutes: data.order_lead_minutes,
          zones: (data.zones || []).map(zone => ({
            delivery_range_id: zone.delivery_range_id,
            delivery_fee: zone.delivery_fee === null ? '' : zone.delivery_fee
          }))
        }
      },
      /* ---------------- Delivery ---------------- */
      addHour () {
        const last = this.form.delivery_hours[this.form.delivery_hours.length - 1]
        this.form.delivery_hours.push(last ? { from: last.to === '24:00' ? '' : last.to, to: '' } : { from: '12:00', to: '13:00' })
      },
      isZoneSelected (id) {
        return !!this.selectedZone(id)
      },
      selectedZone (id) {
        return this.form.zones.find(zone => zone.delivery_range_id === id)
      },
      toggleZone (zone) {
        const index = this.form.zones.findIndex(item => item.delivery_range_id === zone.id)
        if (index === -1) {
          this.form.zones.push({ delivery_range_id: zone.id, delivery_fee: '' })
        } else {
          this.form.zones.splice(index, 1)
        }
      },
      /* ---------------- Image ---------------- */
      handleImageChange (file, fileList) {
        if (file.size && (file.size / 1024 / 1024) > MAX_IMAGE_MB) {
          swal({ toast: true, type: 'warning', text: this.$t('validations.pictureSizeExceed'), position: 'top-end', showConfirmButton: false, timer: 3000 }).catch(() => {})
          this.form.imagelist = fileList.filter(f => f.uid !== file.uid).slice(-1)
          return
        }
        this.form.imagelist = fileList.slice(-1)
      },
      handleImageRemove () {
        this.form.imagelist = []
      },
      handleExceed () {
        swal('', this.$t('validations.pictureSizeExceed'), 'warning').catch(() => {})
      },
      async collectImage () {
        const f = this.form.imagelist && this.form.imagelist[0]
        if (!f) {
          return ''
        }
        if (f.raw) {
          return this.getBase64(f.raw)
        }
        return f.filename || ''
      },
      /* ---------------- Save ---------------- */
      validate () {
        const errors = {}
        if (!(this.form.name || '').trim()) {
          errors.name = this.$t('menuSubscriptions.nameRequired')
        }
        if (!this.form.location_id) {
          errors.location_id = this.$t('menuSubscriptions.locationRequired')
        }
        const price = parseFloat(this.form.price_per_day)
        if (this.form.price_per_day === '' || isNaN(price) || price < 0) {
          errors.price_per_day = this.$t('menuSubscriptions.priceRequired')
        }
        if (this.form.delivery_hours.some(hour => !hour.from || !hour.to || hour.from >= hour.to)) {
          errors.delivery_hours = this.$t('menuSubscriptions.hoursInvalid')
        }
        this.errors = errors
        if (Object.keys(errors).length) {
          // show the tab that has the error
          const generalKeys = ['name', 'location_id', 'price_per_day']
          swal('', this.$t('menuSubscriptions.fixErrors') + ' (' + (Object.keys(errors).some(key => generalKeys.indexOf(key) !== -1) ? this.$t('menuSubscriptions.tabGeneral') : this.$t('menuSubscriptions.tabDelivery')) + ')', 'warning').catch(() => {})
          return false
        }
        return true
      },
      save () {
        if (!this.validate()) {
          return
        }
        const priceChanged = this.subscriptionId && this.menusCount > 0 &&
          Math.round(parseFloat(this.form.price_per_day) * 100) !== Math.round(parseFloat(this.savedPrice) * 100)
        if (!priceChanged) {
          this.send(false)
          return
        }
        swal({
          title: '',
          text: this.$t('menuSubscriptions.rescaleQuestion', { count: this.menusCount, price: this.form.price_per_day + ' ' + this.currency }),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('menuSubscriptions.rescaleYes'),
          cancelButtonText: this.$t('menuSubscriptions.rescaleNo')
        }).then(() => {
          this.send(true)
        }).catch((dismiss) => {
          if (dismiss === 'cancel') {
            this.send(false)
          }
        })
      },
      async send (rescale) {
        this.saving = true
        let image
        try {
          image = await this.collectImage()
        } catch (e) {
          this.saving = false
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          return
        }

        const data = {
          ...this.requestData(),
          name: this.form.name,
          location_id: this.form.location_id,
          price_per_day: this.form.price_per_day,
          duration_days: String(this.form.duration_days),
          min_quantity: String(this.form.min_quantity),
          max_quantity: String(this.form.max_quantity),
          max_active_subscribers: this.form.max_active_subscribers === '' || this.form.max_active_subscribers === null ? '' : String(this.form.max_active_subscribers),
          description: this.form.description,
          image: image,
          delivery_days: this.form.delivery_days,
          delivery_hours: this.form.delivery_hours,
          order_lead_minutes: String(this.form.order_lead_minutes),
          zones: this.form.zones.map(zone => ({
            delivery_range_id: zone.delivery_range_id,
            delivery_fee: zone.delivery_fee === '' || zone.delivery_fee === null ? null : zone.delivery_fee
          }))
        }
        if (this.subscriptionId) {
          data.active = this.form.active ? 1 : 0
          data.rescale_menus = rescale ? 1 : 0
        }

        const created = !this.subscriptionId
        const url = created ? '/menu-subscription' : '/menu-subscription/' + this.subscriptionId
        this.apiRequest(url, created ? 'POST' : 'PUT', data, (response) => {
          this.saving = false
          if (response.status === true && response.data) {
            this.errors = {}
            this.fillForm(response.data)
            swal({ title: '', text: this.$t('menuSubscriptions.saved'), type: 'success', showConfirmButton: false, timer: 1500 }).catch(() => {})
            this.$emit('saved', response.data, created)
            return
          }
          const errors = response.errors || {}
          const normalized = {}
          Object.keys(errors).forEach(key => {
            normalized[key] = Array.isArray(errors[key]) ? errors[key][0] : errors[key]
          })
          this.errors = normalized
          swal('', Object.values(normalized).join(' ') || this.$t('validations.wentWrong'), 'error').catch(() => {})
        })
      },
      firstError (response) {
        const errors = (response && response.errors) || {}
        const key = Object.keys(errors)[0]
        if (!key) {
          return this.$t('validations.wentWrong')
        }
        return Array.isArray(errors[key]) ? errors[key][0] : errors[key]
      }
    }
  }
</script>
<style>
  .msub-block {
    display: block;
  }
  .msub-mt {
    margin-top: 10px;
  }
  .msub-editor {
    min-height: 220px;
    padding: 0;
  }
  .w-100 {
    width: 100%;
  }
  .msub-days {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .msub-day {
    border: 1px solid #ddd;
    border-radius: 16px;
    padding: 4px 12px;
    cursor: pointer;
    font-weight: normal;
    text-transform: capitalize;
    margin: 0;
  }
  .msub-day.is-checked {
    background: #ede7f6;
    border-color: #b39ddb;
    color: #4527a0;
  }
  .msub-day input {
    margin-right: 4px;
  }
  .msub-hour {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .msub-hour .el-date-editor {
    width: 130px;
  }
  .msub-duration-input {
    max-width: 140px;
  }
  .msub-note {
    margin-top: 6px;
    padding: 8px 10px;
    border-radius: 6px;
    background: #f6f2fc;
    border: 1px solid #e3d9f5;
    color: #666;
    font-size: 12px;
    line-height: 1.5;
  }
  .msub-note > div + div {
    margin-top: 4px;
  }
  .msub-note__estimate {
    color: #4527a0;
    font-weight: 600;
  }
  .msub-lead-hint {
    margin-top: 30px;
    font-size: 13px;
  }
  .msub-zones {
    border: 1px solid #eee;
    border-radius: 6px;
    max-height: 320px;
    overflow-y: auto;
  }
  .msub-zone {
    display: grid;
    grid-template-columns: 30px minmax(180px, 2fr) 120px 140px;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-bottom: 1px solid #f3f3f3;
  }
  .msub-zone--head {
    font-size: 12px;
    color: #999;
  }
  .msub-zone.is-checked {
    background: #f6f2fc;
  }
  .msub-zone__name {
    cursor: pointer;
  }
  .msub-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 11px;
    background: #f5f5f5;
    color: #999;
    border: 1px solid #e0e0e0;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/MenuSubscriptions/MenuSubscriptionForm.vue